import { defineStore } from 'pinia';

import { UserRole } from '@tallo/user';

import { ActivateRenterAccountWithGoogleDto } from './model/activate-renter-account-with-google.dto';
import { CreateUserDto } from './model/create-user.dto';
import { PasswordSetupDto } from './model/password-setup.dto';
import { SetupNewPasswordDto } from './model/setup-new-password.dto';
import { SignInWithCredsDto } from './model/sign-in-with-creds.dto';
import authService from './services/auth.service';
import googleSignInService, { GoogleRenderButtonOptions } from './services/google-sign-in.service';
import tokenService from './services/token.service';

export const useAuthStore = defineStore('auth', {
  state: (): {
    isAuthenticated: boolean;
  } => ({
    isAuthenticated: false,
  }),
  getters: {},
  actions: {
    setupAuthenticatedFlag() {
      this.isAuthenticated = tokenService.getToken() !== null;
    },

    authenticate(accessToken: string): void {
      this.isAuthenticated = !!accessToken;
      tokenService.saveToken(accessToken);
    },

    logout(): void {
      tokenService.removeToken();
      this.isAuthenticated = false;
      googleSignInService.signOut();
    },

    initializeGoogleSignInClient(): void {
      googleSignInService.initialize();
    },

    renderGoogleSignInButton(params: GoogleRenderButtonOptions): void {
      googleSignInService.renderGoogleSignInButton(params);
    },

    async signInWithCredentials(params: SignInWithCredsDto): Promise<void> {
      const { accessToken } = await authService.signInWithCredentials(params);

      this.authenticate(accessToken);
    },

    async sendPasswordResetLink(email: string, forRole: UserRole): Promise<void> {
      await authService.sendPasswordResetLink(email, forRole);
    },

    async confirmPasswordReset(passwordSetupDto: PasswordSetupDto): Promise<void> {
      const { accessToken } = await authService.confirmPasswordReset(passwordSetupDto);

      this.authenticate(accessToken);
    },

    async requestOneTimeSignInCode(email: string): Promise<void> {
      await authService.requestOneTimeSignInCode(email);
    },

    async signInWithOneTimeCode(email: string, code: number): Promise<void> {
      const { accessToken } = await authService.signInWithOneTimeCode(email, code);

      this.authenticate(accessToken);
    },

    async investorSignUpWithCreds(userData: CreateUserDto): Promise<{ userId: string }> {
      return authService.investorSignUpWithCreds(userData);
    },

    async investorConfirmEmail(userId: string, code: number): Promise<{ companyId: string }> {
      const { companyId, accessToken } = await authService.investorConfirmEmail(userId, code);

      this.authenticate(accessToken);

      return { companyId };
    },

    async investorResendEmailConfirmationCode(userId: string): Promise<void> {
      await authService.investorResendEmailConfirmationCode(userId);
    },

    async investorSignInWithGoogle(googleJwt: string): Promise<{ companyId: string | null }> {
      const { companyId, accessToken } = await authService.investorSignInWithGoogle(googleJwt);

      this.authenticate(accessToken);

      return { companyId };
    },

    async investorSignUpWithGoogle(googleJwt: string): Promise<{ companyId: string | null }> {
      const { companyId, accessToken } = await authService.investorSignUpWithGoogle(googleJwt);

      this.authenticate(accessToken);

      return { companyId };
    },

    async renterSignInWithGoogle(googleJwt: string): Promise<void> {
      const { accessToken } = await authService.renterSignInWithGoogle(googleJwt);

      this.authenticate(accessToken);
    },

    async renterActivateWithGoogle(accountActivationDto: ActivateRenterAccountWithGoogleDto): Promise<void> {
      const { accessToken } = await authService.renterActivateWithGoogle(accountActivationDto);

      this.authenticate(accessToken);
    },

    async renterActivateAccount(passwordSetupDto: SetupNewPasswordDto): Promise<void> {
      const { accessToken } = await authService.renterActivateWithCreds(passwordSetupDto);

      this.authenticate(accessToken);
    },
  },
});
